import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Star, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface Question {
  id: string;
  type: 'rating' | 'text' | 'yes_no' | 'multiple_choice';
  question: string;
  required: boolean;
  options?: {
    scale?: number;
    labels?: string[];
    multiline?: boolean;
    choices?: string[];
  };
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  course_id?: string;
}

interface SurveyTakerProps {
  surveyId: string;
  onComplete?: () => void;
}

export function SurveyTaker({ surveyId, onComplete }: SurveyTakerProps) {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadSurvey();
    checkExistingResponse();
  }, [surveyId]);

  const loadSurvey = async () => {
    try {
      // Mock survey data until migration is complete
      const mockSurvey: Survey = {
        id: surveyId,
        title: 'Course Evaluation Survey',
        description: 'Please provide your feedback on this course',
        questions: [
          {
            id: 'overall_rating',
            type: 'rating',
            question: 'Overall, how would you rate this course?',
            required: true,
            options: { scale: 5, labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] }
          },
          {
            id: 'improvements',
            type: 'text',
            question: 'What improvements would you suggest?',
            required: false,
            options: { multiline: true }
          }
        ]
      };
      
      const data = mockSurvey;
      const error = null;

      if (error) throw error;
      setSurvey(data);
    } catch (error) {
      toast({
        title: "Error loading survey",
        description: "Failed to load survey questions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const checkExistingResponse = async () => {
    if (!user) return;

    try {
      // Mock existing response check until migration is complete
      const data = null;
      const error = null;

      // Mock response handling
      if (data) {
        setResponses(data.responses as Record<string, any>);
        setCompleted(true);
      }
    } catch (error) {
      // No existing response found, which is fine
    }
  };

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const saveProgress = async () => {
    if (!user || !survey) return;

    try {
      // Mock save until migration is complete
      const error = null;

      if (error) throw error;
    } catch (error) {
      // Silent fail for auto-save
    }
  };

  const submitSurvey = async () => {
    if (!user || !survey) return;

    // Validate required questions
    const requiredQuestions = survey.questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => !responses[q.id]);

    if (missingAnswers.length > 0) {
      toast({
        title: "Missing required answers",
        description: `Please answer all required questions before submitting.`,
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      // Mock submit until migration is complete
      const error = null;

      if (error) throw error;

      setCompleted(true);
      toast({
        title: "Survey submitted successfully",
        description: "Thank you for your feedback!"
      });

      onComplete?.();
    } catch (error) {
      toast({
        title: "Error submitting survey",
        description: "Failed to submit survey responses",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const value = responses[question.id];

    switch (question.type) {
      case 'rating':
        const scale = question.options?.scale || 5;
        const labels = question.options?.labels || [];
        
        return (
          <div className="space-y-4">
            <div className="flex justify-center space-x-2">
              {Array.from({ length: scale }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handleResponseChange(question.id, i + 1)}
                  className={`p-2 rounded-full transition-colors ${
                    value === i + 1 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <Star 
                    className={`h-6 w-6 ${value === i + 1 ? 'fill-current' : ''}`} 
                  />
                </button>
              ))}
            </div>
            {labels.length > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{labels[0]}</span>
                <span>{labels[labels.length - 1]}</span>
              </div>
            )}
            {value && (
              <div className="text-center text-sm font-medium">
                Rating: {value}/5
                {labels[value - 1] && ` (${labels[value - 1]})`}
              </div>
            )}
          </div>
        );

      case 'text':
        if (question.options?.multiline) {
          return (
            <Textarea
              value={value || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder="Enter your response..."
              className="min-h-[100px]"
            />
          );
        } else {
          return (
            <Input
              value={value || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder="Enter your response..."
            />
          );
        }

      case 'yes_no':
        return (
          <RadioGroup
            value={value}
            onValueChange={(val) => handleResponseChange(question.id, val === 'true')}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`}>Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`}>No</Label>
            </div>
          </RadioGroup>
        );

      case 'multiple_choice':
        return (
          <RadioGroup
            value={value}
            onValueChange={(val) => handleResponseChange(question.id, val)}
          >
            {question.options?.choices?.map((choice, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={choice} id={`${question.id}-${index}`} />
                <Label htmlFor={`${question.id}-${index}`}>{choice}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      default:
        return <div>Unsupported question type</div>;
    }
  };

  // Auto-save progress every 10 seconds
  useEffect(() => {
    const interval = setInterval(saveProgress, 10000);
    return () => clearInterval(interval);
  }, [responses, user, survey]);

  if (loading) {
    return <div className="flex justify-center p-8">Loading survey...</div>;
  }

  if (!survey) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">Survey not found.</p>
        </CardContent>
      </Card>
    );
  }

  if (completed) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-muted-foreground">
            Your survey response has been submitted successfully.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currentQuestion = survey.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / survey.questions.length) * 100;
  const isLastQuestion = currentQuestionIndex === survey.questions.length - 1;
  const canProceed = !currentQuestion.required || responses[currentQuestion.id] !== undefined;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <CardTitle>{survey.title}</CardTitle>
            <span className="text-sm text-muted-foreground">
              {currentQuestionIndex + 1} of {survey.questions.length}
            </span>
          </div>
          <Progress value={progress} className="w-full" />
          {survey.description && (
            <p className="text-sm text-muted-foreground">{survey.description}</p>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-4">
              {currentQuestion.question}
              {currentQuestion.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h3>
            {renderQuestion(currentQuestion)}
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={submitSurvey}
                disabled={!canProceed || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Survey'}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (canProceed) {
                    setCurrentQuestionIndex(prev => prev + 1);
                  }
                }}
                disabled={!canProceed}
              >
                Next
              </Button>
            )}
          </div>

          {!canProceed && currentQuestion.required && (
            <p className="text-sm text-red-500 text-center">
              This question is required. Please provide an answer to continue.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}